/**
 * Payment Provider Utilities
 * 
 * Helper functions for detecting and configuring payment providers (Stripe, Square).
 * Improved version from mobile-app with proper TypeScript types and better error handling.
 * 
 * @module lib/utils/paymentProvider
 */

import type { PaymentSettings } from '@lgc/database/types';

// Provider identity + client renderability are the shared cross-runtime primitives.
// Re-exported so existing `~/lib/utils/paymentProvider` consumers keep their import path.
// `detectPaymentProvider` is also used locally by `getPaymentProviderConfig` below.
import { detectPaymentProvider, isPaymentProviderConfigured } from '@butter/library/payment';
export { detectPaymentProvider, isPaymentProviderConfigured };

/**
 * Safely extract a club's payment settings from its `settings` JSON.
 *
 * The `Club` type stores `settings` as a JSON column whose inner shape is not
 * fully typed, so consumers would otherwise need `as any` / `as unknown as`
 * casts. This performs the cast in one place and returns a typed result —
 * mirroring `getAppliedTaxRate` in `lib/utils/taxCalculation.ts`.
 *
 * @param club - Any object with an optional `settings` property (e.g. ClubWithTenant, EnrolledClub)
 * @returns The club's `PaymentSettings`, or null when absent
 *
 * @example
 * ```typescript
 * const provider = detectPaymentProvider(getPaymentSettings(homeClub));
 * ```
 */
export function getPaymentSettings(
  club: { settings?: unknown } | null | undefined
): PaymentSettings | null {
  if (!club?.settings) return null;

  const settings = club.settings as { paymentSettings?: PaymentSettings };
  return settings.paymentSettings ?? null;
}

/**
 * Gets the appropriate payment provider configuration for a club
 * 
 * Returns the configuration object for the specified payment provider.
 * Supports both Stripe and Square.
 * 
 * @param paymentSettings - The club's payment settings
 * @param provider - The specific provider to get config for
 * @returns The provider configuration or null if not available
 * 
 * @example
 * ```typescript
 * const stripeConfig = getPaymentProviderConfig(club.settings?.paymentSettings, 'stripe');
 * if (stripeConfig?.publishableKey) {
 *   // Initialize Stripe
 * }
 * ```
 */
export function getPaymentProviderConfig(
  paymentSettings?: PaymentSettings | null,
  provider?: 'stripe' | 'square'
): PaymentSettings['stripe'] | PaymentSettings['square'] | null {
  if (!paymentSettings) {
    return null;
  }

  const targetProvider = provider || detectPaymentProvider(paymentSettings);
  
  if (targetProvider === 'stripe') {
    return paymentSettings.stripe ?? null;
  }
  
  if (targetProvider === 'square') {
    return paymentSettings.square ?? null;
  }

  return null;
}

/**
 * Detects the correct Square environment based on the application ID
 * 
 * Square application IDs that start with 'sandbox-' must use the sandbox environment.
 * All other application IDs are for production.
 * 
 * @param applicationId - The Square application ID
 * @returns The Square environment ('sandbox' | 'production')
 * 
 * @example
 * ```typescript
 * const env = detectSquareEnvironment('sandbox-sq0idp-xxxx');
 * // Returns 'sandbox'
 * ```
 */
export function detectSquareEnvironment(applicationId: string): 'sandbox' | 'production' {
  // Square sandbox application IDs start with 'sandbox-'
  if (applicationId.startsWith('sandbox-')) {
    return 'sandbox';
  }
  return 'production';
}

/**
 * Gets the correct Square Web SDK URL based on the application ID
 * 
 * Automatically selects the correct SDK URL based on whether the application
 * is configured for sandbox or production.
 * 
 * @param applicationId - The Square application ID
 * @returns The URL to load the Square Web SDK
 * 
 * @example
 * ```typescript
 * const sdkUrl = getSquareSdkUrl(squareConfig.applicationId);
 * // Load SDK from this URL
 * ```
 */
export function getSquareSdkUrl(applicationId: string): string {
  const environment = detectSquareEnvironment(applicationId);
  return environment === 'sandbox'
    ? 'https://sandbox.web.squarecdn.com/v1/square.js'
    : 'https://web.squarecdn.com/v1/square.js';
}

/**
 * Gets the Stripe publishable key from config
 * 
 * Helper to safely extract the publishable key for Stripe initialization.
 * 
 * @param stripeConfig - Stripe configuration object
 * @returns The publishable key or null if not available
 * 
 * @example
 * ```typescript
 * const config = getPaymentProviderConfig(paymentSettings, 'stripe');
 * const publishableKey = getStripePublishableKey(config);
 * ```
 */
export function getStripePublishableKey(
  stripeConfig: PaymentSettings['stripe'] | null
): string | null {
  if (!stripeConfig || !('publishableKey' in stripeConfig)) {
    return null;
  }
  return stripeConfig.publishableKey || null;
}

/**
 * Gets a human-readable provider name
 * 
 * @param provider - The provider identifier
 * @returns Formatted provider name for display
 */
export function getProviderDisplayName(provider: 'stripe' | 'square' | null): string {
  switch (provider) {
    case 'stripe':
      return 'Stripe';
    case 'square':
      return 'Square';
    default:
      return 'Payment Provider';
  }
}
